"use client"

import * as React from "react"
import { cn } from "cn"

/**
 * The one dashed edge for empty and suggested things, drawn over its positioned parent. An SVG rather than a CSS
 * dashed border, which picks its own dash and gap. `radius` is the parent's corner radius as a CSS length, such as
 * "var(--radius-2xl)", or half the height for a pill.
 */
function DashedOutline({
  radius = "var(--radius-lg)",
  className,
  style,
  ...props
}: Omit<React.ComponentProps<"svg">, "children"> & {
  radius?: string
}) {
  const svg = React.useRef<SVGSVGElement>(null)
  const [cornerPx, setCornerPx] = React.useState(0)

  // Safari ignores rx as a CSS property, so the radius is resolved to pixels through border-radius and set as the attribute.
  React.useLayoutEffect(() => {
    if (!svg.current) return
    setCornerPx(parseFloat(getComputedStyle(svg.current).borderTopLeftRadius) || 0)
  }, [radius])

  return (
    <svg
      ref={svg}
      data-slot="dashed-outline"
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute inset-0 size-full text-foreground/25 transition-colors duration-200",
        className
      )}
      style={{ borderRadius: radius, ...style }}
      {...props}
    >
      <rect
        x="0.5"
        y="0.5"
        rx={Math.max(cornerPx - 0.5, 0)}
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        strokeDasharray="3 3.5"
        style={{
          width: "calc(100% - 1px)",
          height: "calc(100% - 1px)",
        }}
      />
    </svg>
  )
}

export { DashedOutline }
